// src/services/api.js
import { fetchAuthSession } from 'aws-amplify/auth';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-js';
import { get, post, put } from '@aws-amplify/api-rest';
import { handleApiCall, getAuthHeaders } from './utils';
import awsconfig from '../amplify_outputs.json';

const endpoint_prefix = awsconfig.custom?.API?.CourseBookingHttpApi?.endpoint || 'https://api.colaistecc.com';

const buildEndpoint = (path) => {
    // Remove leading slash from path if endpoint_prefix already has trailing slash
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${endpoint_prefix}${normalizedPath}`;
};

// Export base URL for services that need it
export const API_BASE_URL = endpoint_prefix;

export const signRequest = async (endpoint, method, body = null) => {
    try {
        // The API Gateway uses a Cognito User Pools authorizer, which expects the
        // Cognito ID token as a Bearer header. (Do NOT SigV4-sign — that overwrites
        // the Authorization header with an IAM signature and the authorizer 401s,
        // which the browser then reports as a CORS error.)
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) throw new Error('No authentication token available');

        return {
            'content-type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
        };
    } catch (err) {
        console.error('Error building auth headers:', err);
        throw err;
    }
};

export const fetchData = async (path, method, body = null) => {
    try {
        const endpoint = buildEndpoint(path);
        const headers = await signRequest(endpoint, method, body);
        
        // Log the headers being sent for debugging
        console.log('API Request:', {
            endpoint,
            method,
            headers: {
                ...headers,
                Authorization: headers.Authorization ? 'Bearer [REDACTED]' : 'Missing'
            }
        });

        const fetchOptions = {
            method: method,
            headers: headers,
        };

        if (body) {
            fetchOptions.body = JSON.stringify(body);
        }

        const response = await fetch(endpoint, fetchOptions);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Request failed: ${errorText}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error in fetchData:', err);
        throw err;
    }
};

// Default export with standard REST methods
const api = {
    get: (path) => fetchData(path, 'GET'),
    post: (path, body) => fetchData(path, 'POST', body),
    put: (path, body) => fetchData(path, 'PUT', body),
    patch: (path, body) => fetchData(path, 'PATCH', body),
    delete: (path) => fetchData(path, 'DELETE')
};

// Legacy makeRequest function for backwards compatibility
export const makeRequest = async (url, options = {}) => {
    const method = options.method || 'GET';
    const body = options.body ? JSON.parse(options.body) : null;
    
    // Extract path from full URL
    const urlObj = new URL(url);
    let path = urlObj.pathname + urlObj.search;
    
    // Remove /prod/ prefix if it exists to avoid duplication since endpoint_prefix already includes it
    if (path.startsWith('/prod/')) {
        path = path.substring(6); // Remove '/prod/' (6 characters)
    }
    
    return fetchData(path, method, body);
};

// Audit & Clean Bookings API functions - Using proper auth like other admin functions
const API_NAME = 'CourseBookingApi';
export const auditBookings = (params) => handleApiCall(async function auditBookings() {
    const headers = await getAuthHeaders();
    console.log('Calling auditBookings API with params:', params);
    
    return post({
        apiName: API_NAME,
        path: 'admin/audit-bookings',
        options: {
            headers,
            body: params
        }
    });
});

export const applyBookingFixes = (params) => handleApiCall(async function applyBookingFixes() {
    const headers = await getAuthHeaders();
    console.log('Calling applyBookingFixes API with params:', params);

    return post({
        apiName: API_NAME,
        path: 'admin/audit-bookings/apply',
        options: {
            headers,
            body: params
        }
    });
});

// Admin Student Move API functions - Slot-neutral operations
export const adminMoveStudentPreserveSlot = (params) => handleApiCall(async function adminMoveStudentPreserveSlot() {
    const headers = await getAuthHeaders();
    console.log('Calling adminMoveStudentPreserveSlot API with params:', params);

    return post({
        apiName: API_NAME,
        path: 'admin/students/move-preserve-slot',
        options: {
            headers,
            body: params
        }
    });
});

export const reconcileCourseSlots = (courseId, fix = false) => handleApiCall(async function reconcileCourseSlots() {
    const headers = await getAuthHeaders();
    console.log(`Calling reconcileCourseSlots API for course ${courseId}, fix: ${fix}`);

    return post({
        apiName: API_NAME,
        path: `admin/courses/${courseId}/reconcile-slots`,
        options: {
            headers,
            body: { fix }
        }
    });
});

export const getStudentMoveAuditLog = (filters = {}) => handleApiCall(async function getStudentMoveAuditLog() {
    const headers = await getAuthHeaders();
    const { courseId, studentEmail, bookingId } = filters;

    // Build query string
    const queryParams = new URLSearchParams();
    if (courseId) queryParams.append('courseId', courseId);
    if (studentEmail) queryParams.append('studentEmail', studentEmail);
    if (bookingId) queryParams.append('bookingId', bookingId);

    const queryString = queryParams.toString();
    const path = `admin/audit/student-moves${queryString ? '?' + queryString : ''}`;

    console.log('Calling getStudentMoveAuditLog API with filters:', filters);

    // Use GET request for audit log retrieval
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

// Admin Audit Log API functions - Use getAuthHeaders() for proper Bearer token auth
export const getRecentAuditLogs = (params = {}) => handleApiCall(async function getRecentAuditLogs() {
    const headers = await getAuthHeaders();
    const { limit = 50, action, admin } = params;
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit);
    if (action) queryParams.append('action', action);
    if (admin) queryParams.append('admin', admin);
    const queryString = queryParams.toString();
    const path = `admin/audit/recent${queryString ? '?' + queryString : ''}`;
    console.log('Calling getRecentAuditLogs API');
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getAuditLogsByAdmin = (adminEmail, options = {}) => handleApiCall(async function getAuditLogsByAdmin() {
    const headers = await getAuthHeaders();
    const { limit = 50, startDate, endDate } = options;
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    const path = `admin/audit/by-admin/${encodeURIComponent(adminEmail)}${queryString ? '?' + queryString : ''}`;
    console.log('Calling getAuditLogsByAdmin API for:', adminEmail);
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getAuditLogsByAction = (action, options = {}) => handleApiCall(async function getAuditLogsByAction() {
    const headers = await getAuthHeaders();
    const { limit = 50, startDate, endDate } = options;
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    const path = `admin/audit/by-action/${encodeURIComponent(action)}${queryString ? '?' + queryString : ''}`;
    console.log('Calling getAuditLogsByAction API for:', action);
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getAuditLogsByTarget = (targetType, targetId, options = {}) => handleApiCall(async function getAuditLogsByTarget() {
    const headers = await getAuthHeaders();
    const { limit = 50 } = options;
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit);
    const queryString = queryParams.toString();
    const path = `admin/audit/by-target/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}${queryString ? '?' + queryString : ''}`;
    console.log('Calling getAuditLogsByTarget API for:', targetType, targetId);
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getAuditLogsByEmail = (email, options = {}) => handleApiCall(async function getAuditLogsByEmail() {
    const headers = await getAuthHeaders();
    const { limit = 50 } = options;
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit);
    const queryString = queryParams.toString();
    const path = `admin/audit/by-email/${encodeURIComponent(email)}${queryString ? '?' + queryString : ''}`;
    console.log('Calling getAuditLogsByEmail API for:', email);
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getAuditStats = (options = {}) => handleApiCall(async function getAuditStats() {
    const headers = await getAuthHeaders();
    const { startDate, endDate } = options;
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    const path = `admin/audit/stats${queryString ? '?' + queryString : ''}`;
    console.log('Calling getAuditStats API');
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getAuditActionTypes = () => handleApiCall(async function getAuditActionTypes() {
    const headers = await getAuthHeaders();
    console.log('Calling getAuditActionTypes API');
    return get({
        apiName: API_NAME,
        path: 'admin/audit/action-types',
        options: { headers }
    });
});

// Admin Permissions API functions
export const getAdminUsersWithPermissions = () => handleApiCall(async function getAdminUsersWithPermissions() {
    const headers = await getAuthHeaders();
    console.log('Calling getAdminUsersWithPermissions API');
    return get({
        apiName: API_NAME,
        path: 'admin/permissions/admins',
        options: { headers }
    });
});

export const getMyPermissions = () => handleApiCall(async function getMyPermissions() {
    const headers = await getAuthHeaders();
    console.log('Calling getMyPermissions API');
    return get({
        apiName: API_NAME,
        path: 'admin/permissions/me',
        options: { headers }
    });
});

export const getAdminPermissions = (adminEmail) => handleApiCall(async function getAdminPermissions() {
    const headers = await getAuthHeaders();
    console.log('Calling getAdminPermissions API for:', adminEmail);
    return get({
        apiName: API_NAME,
        path: `admin/permissions/${encodeURIComponent(adminEmail)}`,
        options: { headers }
    });
});

export const updateAdminPermissions = (adminEmail, permissions) => handleApiCall(async function updateAdminPermissions() {
    const headers = await getAuthHeaders();
    console.log('Calling updateAdminPermissions API for:', adminEmail, 'permissions:', permissions);
    return put({
        apiName: API_NAME,
        path: `admin/permissions/${encodeURIComponent(adminEmail)}`,
        options: {
            headers,
            body: { permissions }
        }
    });
});

// Guardian Booking Aggregation API functions (Task #5 Fix)
export const getAllGuardianBookings = async () => {
    return handleApiCall(async () => {
        console.log('Calling getAllGuardianBookings API to fetch all bookings for guardian across all students');
        const headers = await getAuthHeaders();
        const response = await get({
            apiName: API_NAME,
            path: 'guardians/bookings/all',
            options: { headers }
        });
        return response;
    });
};

export const diagnoseGuardianBookings = async (guardianEmail) => {
    return handleApiCall(async () => {
        console.log('Calling diagnoseGuardianBookings API for guardian:', guardianEmail);
        const headers = await getAuthHeaders();

        const queryParams = new URLSearchParams();
        if (guardianEmail) queryParams.append('guardianEmail', guardianEmail);
        const queryString = queryParams.toString();
        const path = `admin/guardians/diagnostic${queryString ? '?' + queryString : ''}`;

        const response = await get({
            apiName: API_NAME,
            path: path,
            options: { headers }
        });
        return response;
    });
};

export const recalculateGuardianBalances = async (guardianEmail, fix = false) => {
    return handleApiCall(async () => {
        console.log(`Calling recalculateGuardianBalances API for guardian: ${guardianEmail}, fix: ${fix}`);
        const headers = await getAuthHeaders();
        const response = await post({
            apiName: API_NAME,
            path: 'admin/guardians/recalculate-balances',
            options: {
                headers,
                body: { guardianEmail, fix }
            }
        });
        return response;
    });
};

// Student Guardian Transfer API functions (Task #2)
export const transferStudentToNewGuardian = async (transferData) => {
    return handleApiCall(async () => {
        console.log('Calling transferStudentToNewGuardian API with data:', transferData);
        const headers = await getAuthHeaders();
        const response = await post({
            apiName: API_NAME,
            path: 'admin/students/transfer',
            options: {
                headers,
                body: transferData
            }
        });
        return response;
    });
};

export const previewStudentTransfer = async (transferData) => {
    return handleApiCall(async () => {
        console.log('Calling previewStudentTransfer API with data:', transferData);
        const headers = await getAuthHeaders();
        const response = await post({
            apiName: API_NAME,
            path: 'admin/students/transfer/preview',
            options: {
                headers,
                body: transferData
            }
        });
        return response;
    });
};

export const getStudentTransferHistory = async (studentEmail) => {
    return handleApiCall(async () => {
        console.log('Calling getStudentTransferHistory API for student:', studentEmail);
        const headers = await getAuthHeaders();
        const path = `admin/students/${encodeURIComponent(studentEmail)}/transfer-history`;
        const response = await get({
            apiName: API_NAME,
            path: path,
            options: { headers }
        });
        return response;
    });
};

// Friend Consolidation & Housing API functions (Tasks #3 & #4)
export const consolidateFriendData = async (courseId) => {
    return handleApiCall(async () => {
        console.log('Calling consolidateFriendData API for course:', courseId);
        const headers = await getAuthHeaders();
        const response = await post({
            apiName: API_NAME,
            path: `admin/courses/${courseId}/friends/consolidate`,
            options: { headers, body: {} }
        });
        return response;
    });
};

export const getFriendCircles = async (courseId) => {
    return handleApiCall(async () => {
        console.log('Calling getFriendCircles API for course:', courseId);
        const headers = await getAuthHeaders();
        const response = await get({
            apiName: API_NAME,
            path: `admin/courses/${courseId}/friends/circles`,
            options: { headers }
        });
        return response;
    });
};

export const assignFriendGroupsToHousing = async (courseId, assignmentData) => {
    return handleApiCall(async () => {
        console.log('Calling assignFriendGroupsToHousing API with data:', assignmentData);
        const headers = await getAuthHeaders();
        const response = await post({
            apiName: API_NAME,
            path: `admin/courses/${courseId}/friends/assign-housing`,
            options: { headers, body: assignmentData }
        });
        return response;
    });
};

export const overrideFriendGroupAssignment = async (courseId, overrideData) => {
    return handleApiCall(async () => {
        console.log('Calling overrideFriendGroupAssignment API with data:', overrideData);
        const headers = await getAuthHeaders();
        const response = await post({
            apiName: API_NAME,
            path: `admin/courses/${courseId}/friends/override`,
            options: { headers, body: overrideData }
        });
        return response;
    });
};

// Exception Report API functions
export const getIncompleteFormBookings = (courseId) => handleApiCall(async function getIncompleteFormBookings() {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams();
    if (courseId) queryParams.append('courseId', courseId);
    const queryString = queryParams.toString();
    const path = `admin/reports/incomplete-forms${queryString ? '?' + queryString : ''}`;
    console.log('Calling getIncompleteFormBookings API');
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getStudentsWithoutApprovedSchool = (courseId) => handleApiCall(async function getStudentsWithoutApprovedSchool() {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams();
    if (courseId) queryParams.append('courseId', courseId);
    const queryString = queryParams.toString();
    const path = `admin/reports/no-school${queryString ? '?' + queryString : ''}`;
    console.log('Calling getStudentsWithoutApprovedSchool API');
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getApprovedSchools = () => handleApiCall(async function getApprovedSchools() {
    const headers = await getAuthHeaders();
    console.log('Calling getApprovedSchools API');
    return get({
        apiName: API_NAME,
        path: 'admin/reports/approved-schools',
        options: { headers }
    });
});

export const assignApprovedSchool = (bookingIds, schoolId, schoolName) => handleApiCall(async function assignApprovedSchool() {
    const headers = await getAuthHeaders();
    console.log('Calling assignApprovedSchool API for bookings:', bookingIds);
    return post({
        apiName: API_NAME,
        path: 'admin/reports/assign-school',
        options: {
            headers,
            body: { bookingIds, schoolId, schoolName }
        }
    });
});

export const getBookingsWithoutImageConsent = (courseId) => handleApiCall(async function getBookingsWithoutImageConsent() {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams();
    if (courseId) queryParams.append('courseId', courseId);
    const queryString = queryParams.toString();
    const path = `admin/reports/no-consent${queryString ? '?' + queryString : ''}`;
    console.log('Calling getBookingsWithoutImageConsent API');
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

export const getPotentialDuplicates = (courseId) => handleApiCall(async function getPotentialDuplicates() {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams();
    if (courseId) queryParams.append('courseId', courseId);
    const queryString = queryParams.toString();
    const path = `admin/reports/duplicates${queryString ? '?' + queryString : ''}`;
    console.log('Calling getPotentialDuplicates API');
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

// Booking Copy API functions (Course Copy Feature)
export const copyBookingToCourse = (bookingId, targetCourseId, copyReason) => handleApiCall(async function copyBookingToCourse() {
    const headers = await getAuthHeaders();
    console.log('Calling copyBookingToCourse API for booking:', bookingId, 'to course:', targetCourseId);
    return post({
        apiName: API_NAME,
        path: `admin/bookings/${bookingId}/copy-to-course`,
        options: {
            headers,
            body: { targetCourseId, copyReason }
        }
    });
});

export const getCopiedBookingsReport = (filters = {}) => handleApiCall(async function getCopiedBookingsReport() {
    const headers = await getAuthHeaders();
    const { courseId, startDate, endDate } = filters;
    const queryParams = new URLSearchParams();
    if (courseId) queryParams.append('courseId', courseId);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    const path = `admin/reports/copied-bookings${queryString ? '?' + queryString : ''}`;
    console.log('Calling getCopiedBookingsReport API');
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

// Scholarship report (Admin Reports → Scholarships). `year` is the academic
// year (year of the course start date), defaults server-side to the current year.
export const getScholarshipReport = (year) => handleApiCall(async function getScholarshipReport() {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams();
    if (year) queryParams.append('year', String(year));
    const queryString = queryParams.toString();
    const path = `admin/reports/scholarships${queryString ? '?' + queryString : ''}`;
    return get({
        apiName: API_NAME,
        path: path,
        options: { headers }
    });
});

// Re-export fetchAdminCourses for convenience
export { fetchAdminCourses } from './courseService';

export default api;
